import { type ReactNode, useMemo } from "react";

import { type ComboboxOption, ColocaComboboxField } from "@/shared/components/forms/ColocaComboboxField";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import useTranslations from "@/shared/lang/useTranslations";

import { useSubdivisionField } from "./useSubdivisionField";

export type SubdivisionFieldProps = {
  /** RHF path of this field — `state` or `owner_state`. */
  name: string;
  /**
   * RHF path of the country field that scopes it. It is a prop rather than a convention because the two are
   * not always named in step: the company address follows `country` while the legal representative's follows
   * `owner_country_code`, and `BusinessHolderForm` renders both on the same screen.
   */
  countryName: string;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
};

/**
 * State / province / department of an address, as a searchable list scoped to the selected country.
 *
 * The backend stores this as free text, so the list is an input affordance, not a constraint — which is why a
 * country with no catalog degrades to a plain text field instead of becoming unfillable. Neither combobox
 * accepts a value outside its options, so without that branch, widening the country selector would strand the
 * user on a required field they could not answer.
 *
 * Lives in `shared/` rather than in a module because `cards` and `clients` both render it, for the same
 * reason the PSE payer step was moved out of `payins`.
 */
export function SubdivisionField({ name, countryName, label, description, disabled }: SubdivisionFieldProps) {
  const { t } = useTranslations("global");
  const { options, filter, isSelectable } = useSubdivisionField(name, countryName);

  // Copied into a mutable array for the field's prop type. Identity only changes with the country, so the
  // combobox does not see a new `items` on every render.
  const items = useMemo<ComboboxOption[]>(() => [...options], [options]);

  if (!isSelectable) {
    return <ColocaInputField name={name} label={label} description={description} disabled={disabled} />;
  }

  return (
    <ColocaComboboxField
      name={name}
      label={label}
      description={description}
      disabled={disabled}
      items={items}
      filter={filter}
      placeholder={t("subdivision_placeholder")}
      emptyMessage={t("subdivision_empty")}
    />
  );
}
